import { Fragment, useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  FaUserPlus, FaPencilAlt, FaArrowLeft, FaTimes, FaSearch, FaDownload,
  FaChevronDown, FaChevronRight, FaBan, FaCheck,
} from "react-icons/fa";
import Modal from "../Modal";

const API = "/api/administracion/usuarios";

// Solo para mostrar un identificador corto bajo el nombre, tipo "nombre.apellido".
const generarHandle = (nombre, apellidoP) => {
  const limpiar = (t) =>
    (t || "")
      .toLowerCase()
      .normalize("NFD").replace(/[̀-ͯ]/g, "")
      .trim()
      .split(" ")[0];
  const partes = [limpiar(nombre), limpiar(apellidoP)].filter(Boolean);
  return partes.join(".");
};

export default function UsuariosAdmin() {
  const [usuarios, setUsuarios] = useState([]);
  const [rolesDisponibles, setRolesDisponibles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState(false);

  const [busqueda, setBusqueda] = useState("");
  const [rolFiltro, setRolFiltro] = useState("todos");
  const [estatusFiltro, setEstatusFiltro] = useState("todos");
  const [filaExpandida, setFilaExpandida] = useState(null);

  const [notificacion, setNotificacion] = useState({
    mostrar: false,
    mensaje: "",
    tipo: "success"
  });

  const mostrarToast = (mensaje, tipo = "success") => {
    setNotificacion({ mostrar: true, mensaje, tipo });
    setTimeout(() => setNotificacion({ mostrar: false, mensaje: "", tipo: "success" }), 3000);
  };

  // ----------------------
  // Modal de usuario (crear/editar)
  // ----------------------
  const [modalOpen, setModalOpen] = useState(false);
  const [usuarioEditando, setUsuarioEditando] = useState(null);

  const [nuevoUsuario, setNuevoUsuario] = useState({
    nombre: "",
    apellidoP: "",
    apellidoM: "",
    email: "",
    telefono: "",
    password: "",
    avatar: "/avatars/avatar.png",
    estatus: true,
  });

  const avatarsDisponibles = [
    "/avatars/avatar.png",
    "/avatars/avatar1.png",
    "/avatars/avatar2.png",
    "/avatars/avatar3.png",
    "/avatars/avatar4.png",
    "/avatars/avatar5.png",
    "/avatars/avatar6.png",
    "/avatars/avatar7.png",
    "/avatars/avatar8.png",
    "/avatars/avatar9.png"
  ];

  const abrirModal = () => {
    setUsuarioEditando(null); // modo crear
    setNuevoUsuario({
      nombre: "",
      apellidoP: "",
      apellidoM: "",
      email: "",
      telefono: "",
      password: "",
      avatar: "/avatars/avatar.png",
      estatus: true
    });
    setModalOpen(true);
  };

  const abrirModalEditar = (usuario) => {
    setUsuarioEditando(usuario);
    setNuevoUsuario({
      nombre: usuario.nombre || "",
      apellidoP: usuario.ap_paterno || "",
      apellidoM: usuario.ap_materno || "",
      email: usuario.email || "",
      telefono: usuario.telefono || "",
      password: "",
      avatar: usuario.avatar || usuario.icono || "/avatars/avatar.png",
      estatus: usuario.estatus ?? true
    });
    setModalOpen(true);
  };

  const cerrarModal = () => {
    setModalOpen(false);
    setUsuarioEditando(null);
  };

  const guardarUsuario = async () => {
    const { nombre, apellidoP, apellidoM, email, telefono, password, avatar, estatus } = nuevoUsuario;

    if (!nombre.trim() || !email.trim() || (!password.trim() && !usuarioEditando)) {
      mostrarToast("Nombre, email y contraseña son obligatorios", "error");
      return;
    }

    try {
      let res;

      if (usuarioEditando) {
        res = await fetch(`${API}/${usuarioEditando.id_usuario}`, {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            nombre,
            ap_paterno: apellidoP,
            ap_materno: apellidoM,
            email,
            telefono,
            ...(password ? { password } : {}),
            icono: avatar,
            estatus
          }),
        });
      } else {
        res = await fetch(API, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            nombre,
            email,
            telefono,
            password,
            ap_paterno: apellidoP,
            ap_materno: apellidoM,
            icono: avatar
          }),
        });
      }

      if (!res.ok) {
        const data = await res.json();
        mostrarToast(data.message || "Error guardando usuario", "error");
        return;
      }

      mostrarToast(usuarioEditando ? "Usuario actualizado" : "Usuario creado", "success");
      cerrarModal();
      cargar();
    } catch (err) {
      console.error(err);
      mostrarToast("Error inesperado", "error");
    }
  };

  // Activar/desactivar directo desde la tabla (baja lógica: nunca se borra el registro).
  const cambiarEstatusUsuario = async (usuario) => {
    try {
      const res = await fetch(`${API}/${usuario.id_usuario}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ estatus: !usuario.estatus }),
      });

      if (!res.ok) {
        const data = await res.json();
        mostrarToast(data.message || "Error actualizando usuario", "error");
        return;
      }

      mostrarToast(usuario.estatus ? "Usuario desactivado" : "Usuario activado", "success");
      cargar();
    } catch (err) {
      console.error(err);
      mostrarToast("Error inesperado", "error");
    }
  };

  // ----------------------
  // Cargar usuarios y roles
  // ----------------------
  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const resUsuarios = await fetch(API, { credentials: "include" });
      const usuariosData = await resUsuarios.json();

      const resRoles = await fetch(`${API}/roles`, { credentials: "include" });
      const rolesData = await resRoles.json();
      setRolesDisponibles(rolesData);

      const grouped = usuariosData.reduce((acc, curr) => {
        const existing = acc.find(u => u.id_usuario === curr.id_usuario);
        if (existing) {
          if (curr.status === 1) existing.roles.push({ id_rol_usuario: curr.id_rol_usuario, rol: curr.rol });
        } else {
          const avatar = curr.icono
            ? (curr.icono.startsWith("/avatars/") ? curr.icono : `/avatars/${curr.icono}`)
            : "/avatars/avatar.png";

          acc.push({
            id_usuario: curr.id_usuario,
            nombre: curr.nombre || "",
            ap_paterno: curr.ap_paterno || "",
            ap_materno: curr.ap_materno || "",
            email: curr.email || "",
            telefono: curr.telefono || "",
            estatus: curr.estatus ?? true,
            roles: curr.status === 1 ? [{ id_rol_usuario: curr.id_rol_usuario, rol: curr.rol }] : [],
            avatar
          });
        }
        return acc;
      }, []);

      setUsuarios(grouped);
    } catch (err) {
      console.error(err);
      mostrarToast("Error cargando usuarios", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // ----------------------
  // Roles
  // ----------------------
  const quitarRol = async (usuarioId, idRolUsuario) => {
    try {
      const res = await fetch(`${API}/roles_usuario/${idRolUsuario}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ estatus: 0 }),
      });

      if (!res.ok) {
        const data = await res.json();
        mostrarToast(data.message || "Error quitando rol", "error");
        return;
      }

      setUsuarios(prev =>
        prev.map(u =>
          u.id_usuario === usuarioId
            ? { ...u, roles: u.roles.filter(r => r.id_rol_usuario !== idRolUsuario) }
            : u
        )
      );

      mostrarToast("Rol quitado", "success");
    } catch (err) {
      console.error(err);
      mostrarToast("Error quitando rol", "error");
    }
  };

  const agregarRol = async (usuarioId, idRol) => {
    if (!idRol) return;
    const usuario = usuarios.find(u => u.id_usuario === usuarioId);
    const rolObj = rolesDisponibles.find(r => r.id_rol === parseInt(idRol));
    if (!rolObj) return;

    const rolExistente = usuario.roles.find(r => r.rol === rolObj.nombre);

    try {
      if (rolExistente) {
        const res = await fetch(`${API}/roles_usuario/${rolExistente.id_rol_usuario}`, {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ estatus: 1 }),
        });

        if (!res.ok) {
          const data = await res.json();
          mostrarToast(data.message || "Error reactivando rol", "error");
          return;
        }

        mostrarToast("Rol reactivado", "success");
        cargar();
      } else {
        const res = await fetch(`${API}/rol`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id_usuario: usuarioId, id_rol: parseInt(idRol) }),
        });

        if (!res.ok) {
          const data = await res.json();
          mostrarToast(data.message || "Error asignando rol", "error");
          return;
        }

        const data = await res.json();
        setUsuarios(prev =>
          prev.map(u =>
            u.id_usuario === usuarioId
              ? { ...u, roles: [...u.roles, { id_rol_usuario: data.id_rol_usuario, rol: rolObj.nombre }] }
              : u
          )
        );

        mostrarToast("Rol agregado", "success");
      }
    } catch (err) {
      console.error(err);
      mostrarToast("Error inesperado al asignar rol", "error");
    }
  };

  // ----------------------
  // Filtros (cliente, el catálogo de usuarios de una iglesia es pequeño)
  // ----------------------
  const usuariosFiltrados = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();

    return usuarios.filter((u) => {
      if (estatusFiltro === "activos" && !u.estatus) return false;
      if (estatusFiltro === "inactivos" && u.estatus) return false;

      if (rolFiltro !== "todos" && !u.roles.some((r) => r.rol === rolFiltro)) return false;

      if (texto) {
        const handle = generarHandle(u.nombre, u.ap_paterno);
        const nombreCompleto = `${u.nombre} ${u.ap_paterno} ${u.ap_materno}`.toLowerCase();
        if (!nombreCompleto.includes(texto) && !u.email.toLowerCase().includes(texto) && !handle.includes(texto)) {
          return false;
        }
      }

      return true;
    });
  }, [usuarios, busqueda, rolFiltro, estatusFiltro]);

  const exportarCSV = () => {
    setExportando(true);
    try {
      const encabezados = ["Nombre", "Correo", "Teléfono", "Roles", "Estatus"];
      const lineas = [encabezados.join(",")];

      usuariosFiltrados.forEach((u) => {
        const fila = [
          `${u.nombre} ${u.ap_paterno} ${u.ap_materno}`.trim(),
          u.email,
          u.telefono || "",
          u.roles.map((r) => r.rol).join(" | "),
          u.estatus ? "Activo" : "Inactivo",
        ];
        lineas.push(fila.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","));
      });

      const csv = "﻿" + lineas.join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "usuarios.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setExportando(false);
    }
  };

  return (
    <div className="p-6 bg-slate-50">

      {/* BREADCRUMB */}
      <div className="text-sm text-slate-400 mb-3">
        <Link to="/dashboard/administracion" className="hover:text-indigo-600">Administración</Link>
        <span className="mx-1.5">/</span>
        <span className="text-slate-600 font-medium">Usuarios</span>
      </div>

      {/* Encabezado */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Usuarios</h1>
          <p className="text-sm text-slate-500 mt-0.5">Gestiona los usuarios que pueden acceder al sistema.</p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={exportarCSV}
            disabled={exportando}
            className="flex items-center gap-2 bg-white border border-slate-200 text-slate-600 text-sm font-medium px-4 py-2 rounded-lg hover:bg-slate-50 transition disabled:opacity-50"
          >
            <FaDownload className="text-xs" /> {exportando ? "Exportando..." : "Exportar"}
          </button>
          <button
            onClick={abrirModal}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition"
          >
            <FaUserPlus className="text-xs" /> Nuevo usuario
          </button>
        </div>
      </div>

      {notificacion.mostrar && (
        <div
          className={`fixed top-5 right-5 z-[70] px-4 py-3 rounded-lg shadow-lg text-sm text-white ${
            notificacion.tipo === "success" ? "bg-emerald-600" : "bg-rose-600"
          }`}
        >
          {notificacion.mensaje}
        </div>
      )}

      {/* BARRA DE FILTROS */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 text-sm" />
          <input
            placeholder="Buscar por nombre, correo o usuario..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
          />
        </div>

        <div>
          <label className="text-xs text-slate-500 mb-1 block">Rol</label>
          <select
            value={rolFiltro}
            onChange={(e) => setRolFiltro(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 max-w-[180px]"
          >
            <option value="todos">Todos</option>
            {rolesDisponibles.map((r) => (
              <option key={r.id_rol} value={r.nombre}>{r.nombre}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-xs text-slate-500 mb-1 block">Estatus</label>
          <select
            value={estatusFiltro}
            onChange={(e) => setEstatusFiltro(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
          >
            <option value="todos">Todos</option>
            <option value="activos">Activos</option>
            <option value="inactivos">Inactivos</option>
          </select>
        </div>
      </div>

      {/* Modal */}
      <Modal
        open={modalOpen}
        onClose={cerrarModal}
        title={usuarioEditando ? "Editar usuario" : "Nuevo usuario"}
      >
        <div className="flex flex-col gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Nombre <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              placeholder="Nombre"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
              value={nuevoUsuario.nombre}
              onChange={e => setNuevoUsuario({ ...nuevoUsuario, nombre: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Apellido paterno</label>
              <input
                type="text"
                placeholder="Apellido paterno"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                value={nuevoUsuario.apellidoP}
                onChange={e => setNuevoUsuario({ ...nuevoUsuario, apellidoP: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Apellido materno</label>
              <input
                type="text"
                placeholder="Apellido materno"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                value={nuevoUsuario.apellidoM}
                onChange={e => setNuevoUsuario({ ...nuevoUsuario, apellidoM: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Email <span className="text-rose-500">*</span>
              </label>
              <input
                type="email"
                placeholder="usuario@correo.com"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                value={nuevoUsuario.email}
                onChange={e => setNuevoUsuario({ ...nuevoUsuario, email: e.target.value })}
                autoComplete="off"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Teléfono</label>
              <input
                type="tel"
                placeholder="10 dígitos"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                value={nuevoUsuario.telefono}
                onChange={e => setNuevoUsuario({ ...nuevoUsuario, telefono: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {usuarioEditando ? "Nueva contraseña (opcional)" : "Contraseña"}
              {!usuarioEditando && <span className="text-rose-500"> *</span>}
            </label>
            <input
              type="password"
              placeholder="********"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
              value={nuevoUsuario.password}
              onChange={e => setNuevoUsuario({ ...nuevoUsuario, password: e.target.value })}
              autoComplete="new-password"
            />
          </div>

          {usuarioEditando && (
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input
                type="checkbox"
                checked={nuevoUsuario.estatus}
                onChange={e => setNuevoUsuario({ ...nuevoUsuario, estatus: e.target.checked })}
                className="accent-indigo-600"
              />
              Usuario activo
            </label>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Avatar</label>
            <div className="grid grid-cols-5 gap-2">
              {avatarsDisponibles.map(a => (
                <img
                  key={a}
                  src={a}
                  alt="avatar"
                  className={`w-11 h-11 rounded-full border-2 cursor-pointer transition ${
                    nuevoUsuario.avatar === a ? "border-indigo-500" : "border-transparent hover:border-slate-300"
                  }`}
                  onClick={() => setNuevoUsuario({ ...nuevoUsuario, avatar: a })}
                />
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-2 pt-4 border-t border-slate-100">
            <button
              onClick={cerrarModal}
              className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
            >
              Cancelar
            </button>
            <button
              onClick={guardarUsuario}
              className="px-4 py-2 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium transition"
            >
              {usuarioEditando ? "Actualizar" : "Guardar"}
            </button>
          </div>
        </div>
      </Modal>

      {/* TABLA */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Usuario</th>
                <th className="px-4 py-3 text-left font-semibold">Correo electrónico</th>
                <th className="px-4 py-3 text-left font-semibold">Teléfono</th>
                <th className="px-4 py-3 text-left font-semibold">Rol</th>
                <th className="px-4 py-3 text-left font-semibold">Estatus</th>
                <th className="px-4 py-3 text-right font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-10 text-slate-400">Cargando usuarios...</td>
                </tr>
              ) : usuariosFiltrados.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-10 text-slate-400">No se encontraron usuarios.</td>
                </tr>
              ) : (
                usuariosFiltrados.map((u) => {
                  const rolesPendientes = rolesDisponibles.filter(r => !u.roles.some(ur => ur.rol === r.nombre));
                  const expandido = filaExpandida === u.id_usuario;

                  return (
                    <Fragment key={u.id_usuario}>
                      <tr className="border-t border-slate-100 hover:bg-slate-50 transition">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <img
                              src={u.avatar || u.icono || "/avatars/avatar.png"}
                              alt="avatar"
                              className="w-9 h-9 rounded-full border border-slate-200 shrink-0"
                              onError={(e) => { e.target.src = "/avatars/avatar.png"; }}
                            />
                            <div className="min-w-0">
                              <p className="font-medium text-slate-800 truncate">{`${u.nombre} ${u.ap_paterno} ${u.ap_materno}`.trim()}</p>
                              <p className="text-xs text-slate-400 truncate">{generarHandle(u.nombre, u.ap_paterno)}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-600">{u.email}</td>
                        <td className="px-4 py-3 text-slate-600">{u.telefono || "—"}</td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => setFilaExpandida(expandido ? null : u.id_usuario)}
                            className="flex items-center gap-1.5 bg-indigo-50 text-indigo-700 px-2.5 py-1 rounded-md text-xs font-medium hover:bg-indigo-100 transition"
                          >
                            {expandido ? <FaChevronDown className="text-[10px]" /> : <FaChevronRight className="text-[10px]" />}
                            {u.roles.length === 0
                              ? "Sin módulos"
                              : u.roles.length === 1
                              ? u.roles[0].rol
                              : `${u.roles[0].rol} +${u.roles.length - 1}`}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <span className="flex items-center gap-2 text-slate-600">
                            <span className={`w-2 h-2 rounded-full ${u.estatus ? "bg-emerald-500" : "bg-slate-300"}`} />
                            {u.estatus ? "Activo" : "Inactivo"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => abrirModalEditar(u)}
                              title="Editar"
                              className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-200 transition"
                            >
                              <FaPencilAlt className="text-xs" />
                            </button>
                            <button
                              onClick={() => cambiarEstatusUsuario(u)}
                              title={u.estatus ? "Desactivar" : "Activar"}
                              className={`w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 transition ${
                                u.estatus ? "hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200" : "hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200"
                              }`}
                            >
                              {u.estatus ? <FaBan className="text-xs" /> : <FaCheck className="text-xs" />}
                            </button>
                          </div>
                        </td>
                      </tr>

                      {expandido && (
                        <tr className="bg-slate-50/70 border-t border-slate-100">
                          <td colSpan={6} className="px-4 py-4">
                            <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Módulos asignados</p>
                            <div className="flex gap-2 flex-wrap items-center">
                              {u.roles.map(r => (
                                <span
                                  key={`${r.id_rol_usuario}-${r.rol}`}
                                  className="flex items-center gap-1 bg-indigo-50 text-indigo-700 px-2 py-1 rounded-md text-xs font-medium"
                                >
                                  {r.rol}
                                  <button
                                    onClick={() => quitarRol(u.id_usuario, r.id_rol_usuario)}
                                    className="text-indigo-400 hover:text-rose-600 font-bold leading-none"
                                    title="Quitar módulo"
                                  >
                                    <FaTimes className="text-[10px]" />
                                  </button>
                                </span>
                              ))}
                              {u.roles.length === 0 && (
                                <span className="text-xs text-slate-400">Este usuario no tiene módulos asignados.</span>
                              )}
                              {rolesPendientes.length > 0 && (
                                <select
                                  className="text-xs px-2 py-1 border border-slate-200 rounded-md text-slate-600 bg-white"
                                  onChange={e => agregarRol(u.id_usuario, e.target.value)}
                                  value=""
                                >
                                  <option value="" disabled>+ Agregar módulo</option>
                                  {rolesPendientes.map(r => <option key={r.id_rol} value={r.id_rol}>{r.nombre}</option>)}
                                </select>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-sm text-slate-500 mt-4">
        Mostrando {usuariosFiltrados.length} de {usuarios.length} usuarios
      </p>
    </div>
  );
}
